import React, { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { assignBadge, getMyStages, getUser } from '../../../api/supabaseApi'
import useApi from '../../../hooks/useApi'
import CourseTable from '../../../components/common/CourseTable'
import countryList from 'react-select-country-list'
import { AuthContext } from '../../../context/AuthContext'
import toast from 'react-hot-toast'

const UserDetails = () => {
  const { id } = useParams()
  const {
    session: { access_token },
  } = useContext(AuthContext)
  const [assignLoading, setAssignLoading] = useState(false)
  const [reloadCourse, setReloadCourse] = useState(false)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const { data: user, loading, error } = useApi(() => getUser(id), [id])
  const { data: { stages, count } = {}, loading: stageLoading } = useApi(
    () => getMyStages(id, page, rowsPerPage),
    [id, page, rowsPerPage, reloadCourse]
  )

  if (loading) {
    return <p>Loading...</p>
  }

  if (error) {
    return <p>Error: {error}</p>
  }

  if (!user) {
    return <p>User not found</p>
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const handlePageChange = (page) => {
    setPage(page)
  }

  const handlePerRowsChange = async (newPerPage) => {
    if (newPerPage === rowsPerPage) return
    setRowsPerPage(newPerPage)
    setPage(1)
  }

  const handleAssignBadge = (badgeSelected) => {
    setAssignLoading(true)
    const badgeIds = Object.values(badgeSelected).reduce(
      (acc, curr) => {
        if (curr.stage) {
          acc.stage = acc.stage.concat(curr.stage);
        }

        if (curr.course) {
          acc.course = acc.course.concat(curr.course);
        }

        return acc;
      },
      { stage: [], course: [] }
    );

    assignBadge(id, badgeIds, access_token).then((res) => {
      if (res.error) {
        toast.custom((t) => (
          <div className="rounded-xl bg-red-700 py-2 px-4 text-white">
            {res.error}
          </div>
        ))
      } else {
        toast.custom((t) => (
          <div className="rounded-xl bg-[#12D18E] py-2 px-4 text-white">
            Changes saved successfully
          </div>
        ))
      }

      setAssignLoading(false)
      setReloadCourse(!reloadCourse)
    })
  }

  return (
    <div className="container mx-auto p-4">
      <div className="p-4 bg-primary rounded-lg mb-4">
        <p className="text-2xl text-white font-bold my-4">User details</p>
      </div>

      <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <img
            src={
              user?.avatars?.readyplayer_url
                ? user.avatars.readyplayer_url.replace('.glb', '.png')
                : '/images/avatar_default.png'
            }
            alt="avatar"
            className="w-16 h-16 rounded-full object-top object-cover bg-[#B0CBBE]"
          />
          <div className="text-center md:text-left">
            <h2 className="text-xl font-bold">{user.full_name}</h2>
            <p className="text-[#7C8FAC]">User ID #{user.id}</p>
          </div>
        </div>
        <div className="mt-4 ">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[#7C8FAC]">Date of birth</p>
              <p>
                {user.date_of_birth ? formatDate(user.date_of_birth) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-[#7C8FAC]">Email address</p>
              <p>{user.real_email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#7C8FAC]">Country</p>
              <p>{countryList().getLabel(user.country || '') || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#7C8FAC]">Age</p>
              <p>{user.age || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#7C8FAC]">Name of organization / School</p>
              <p>{user.organization || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 border border-gray-200 rounded-lg mt-4 shadow-sm">
        <h2 className="text-xl font-bold">Courses progress</h2>
        <CourseTable
          loading={stageLoading}
          stages={stages}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={count}
          handlePageChange={handlePageChange}
          paginationTotalRows={rowsPerPage}
          handlePerRowsChange={handlePerRowsChange}
          handleAssignBadge={handleAssignBadge}
          assignLoading={assignLoading}
        />
      </div>
    </div>
  )
}

export default UserDetails
